import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { isDemoLink, pad } from '@jobfair/shared';
import { env } from '../lib/env';
import { fmtParts } from '../lib/format';
import { toast, useToasts } from '../lib/toast';
import { Icon } from './Icon';

interface ExtLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick' | 'href'> {
  href: string;
  /** Human label for the demo-link notice. */
  label: string;
  /** Called when the link is activated, for analytics. */
  onOpen?: () => void;
  children: ReactNode;
}

/**
 * Opens in a new tab. Placeholder demo links (only recognised in demo builds) show a notice instead of navigating,
 * so a real production link can never be mistaken for one.
 */
export function ExtLink({ href, label, onOpen, children, ...rest }: ExtLinkProps) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (env.demo && isDemoLink(href)) {
      e.preventDefault();
      toast(`קישור הדגמה: ${label} ייפתח בלשונית חדשה, ואתר היריד יישאר פתוח בלשונית הזו.`, 'info');
    }
    onOpen?.();
  };
  return (
    <a {...rest} href={href} target="_blank" rel="noopener" onClick={onClick}>
      {children}
    </a>
  );
}

export function Countdown({ ms }: { ms: number }) {
  const p = fmtParts(ms);
  const cells: [number, string][] =
    p.d > 0
      ? [
          [p.d, 'ימים'],
          [p.h, 'שעות'],
          [p.m, 'דקות'],
        ]
      : [
          [p.h, 'שעות'],
          [p.m, 'דקות'],
          [p.sec, 'שניות'],
        ];
  return (
    <div
      className="countdown"
      role="timer"
      aria-label={cells.map((c) => `${c[0]} ${c[1]}`).join(', ')}
    >
      {cells.map(([v, label]) => (
        <div key={label}>
          <b>{pad(v)}</b>
          <small>{label}</small>
        </div>
      ))}
    </div>
  );
}

const TOAST_ICON = { ok: 'check', err: 'alert', info: 'info', warn: 'alert' } as const;

export function Toasts() {
  const items = useToasts();
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`}>
          <span className="ti">
            <Icon n={TOAST_ICON[t.kind]} />
          </span>
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}
